import React from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Crown, X, AlertCircle, Play } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import SubscriptionService from '@/utils/services/SubscriptionService'

const SubscriptionUpgradeModal = ({ isOpen, onClose, feature }) => {
  const navigate = useNavigate()
  const { currentUserData } = useAuth()

  if (!isOpen) return null

  // Check if user has no plan (no subscription and no trial)
  const hasNoPlan = !currentUserData?.subscriptions?.length && 
                   !currentUserData?.trialData?.endDate && 
                   !SubscriptionService.hasUsedTrial(currentUserData)

  const getFeatureInfo = () => {
    // Special case for users with no plan
    if (hasNoPlan) {
      switch (feature) {
        case 'screens':
          return {
            title: 'Create More Screens',
            description: 'You can create up to 2 screens with your free plan. To create more screens, start your free trial or upgrade to a premium plan.',
            icon: <Play className="w-12 h-12 text-blue-500" />,
            buttonText: 'Start Trial',
            buttonAction: 'trial'
          }
        case 'devices':
          return {
            title: 'Add More Devices',
            description: 'You can connect 1 device with your free plan. To connect more devices, start your free trial or upgrade to a premium plan.',
            icon: <Play className="w-12 h-12 text-blue-500" />,
            buttonText: 'Start Trial',
            buttonAction: 'trial'
          }
        case 'schedules':
          return {
            title: 'Start Your Free Trial',
            description: 'In order to create schedules, please start your free trial. You\'ll get access to all features for 14 days, no credit card required.',
            icon: <Play className="w-12 h-12 text-blue-500" />,
            buttonText: 'Start Trial',
            buttonAction: 'trial'
          }
        case 'video_clips':
          return {
            title: 'Start Your Free Trial',
            description: 'Video clip creation is available with a subscription. Start your free trial to create video clips and unlock all premium features for 14 days, no credit card required.',
            icon: <Play className="w-12 h-12 text-blue-500" />,
            buttonText: 'Start Trial',
            buttonAction: 'trial'
          }
        case 'ai_features':
          return {
            title: 'Start Your Free Trial',
            description: 'AI content generation is available with a subscription. Start your free trial to use AI features and unlock all premium features for 14 days, no credit card required.',
            icon: <Play className="w-12 h-12 text-blue-500" />,
            buttonText: 'Start Trial',
            buttonAction: 'trial'
          }
        case 'analytics':
          return {
            title: 'Start Your Free Trial',
            description: 'Analytics and business intelligence features are available with a subscription. Start your free trial to access detailed analytics and unlock all premium features for 14 days, no credit card required.',
            icon: <Play className="w-12 h-12 text-blue-500" />,
            buttonText: 'Start Trial',
            buttonAction: 'trial'
          }
        default:
          return {
            title: 'Start Your Free Trial',
            description: 'In order to use this feature, please start your free trial. You\'ll get access to all features for 14 days, no credit card required.',
            icon: <Play className="w-12 h-12 text-blue-500" />,
            buttonText: 'Start Trial',
            buttonAction: 'trial'
          }
      }
    }

    // Existing cases for users with plans
    switch (feature) {
      case 'screens':
        return {
          title: 'Upgrade to Create More Screens',
          description: 'You\'ve reached the limit for creating screens with your current plan. Upgrade to a premium plan to create unlimited screens and unlock all features.',
          icon: <Crown className="w-12 h-12 text-yellow-500" />,
          buttonText: 'View Plans',
          buttonAction: 'upgrade'
        }
      case 'devices':
        return {
          title: 'Upgrade to Add More Devices',
          description: 'You\'ve reached the device limit for your current plan. Upgrade to a premium plan to connect more devices and expand your digital signage network.',
          icon: <Crown className="w-12 h-12 text-yellow-500" />,
          buttonText: 'View Plans',
          buttonAction: 'upgrade'
        }
      case 'schedules':
        return {
          title: 'Upgrade to Create Schedules',
          description: 'Scheduling features are available with premium plans. Upgrade to create and manage schedules for your digital signage content.',
          icon: <Crown className="w-12 h-12 text-yellow-500" />,
          buttonText: 'View Plans',
          buttonAction: 'upgrade'
        }
      case 'video_clips':
        return {
          title: 'Upgrade to Create Video Clips',
          description: 'Video clip creation is available with premium subscriptions. Upgrade your plan to create video clips and unlock all premium features.',
          icon: <Crown className="w-12 h-12 text-yellow-500" />,
          buttonText: 'View Plans',
          buttonAction: 'upgrade'
        }
      default:
        return {
          title: 'Upgrade Your Plan',
          description: 'This feature requires a premium subscription. Upgrade your plan to unlock all features and remove limitations.',
          icon: <Crown className="w-12 h-12 text-yellow-500" />,
          buttonText: 'View Plans',
          buttonAction: 'upgrade'
        }
    }
  }

  const featureInfo = getFeatureInfo()

  const handleUpgrade = () => {
    onClose()
    if (featureInfo.buttonAction === 'trial') {
      // Navigate to subscriptions page with trial focus
      navigate('/subscriptions', { state: { focusTrial: true } })
    } else {
      // Navigate to subscriptions page normally
      navigate('/subscriptions')
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4" onClick={onClose}>
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-900">Upgrade Required</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <div className="flex justify-center mb-4">
            {featureInfo.icon}
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {featureInfo.title}
          </h3>
          
          <p className="text-gray-600 mb-6 leading-relaxed">
            {featureInfo.description}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={handleUpgrade}
            className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${
              featureInfo.buttonAction === 'trial' 
                ? 'bg-blue-500 hover:bg-blue-600' 
                : 'bg-yellow-500 hover:bg-yellow-600'
            }`}
          >
            {featureInfo.buttonText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default SubscriptionUpgradeModal 